import { format } from "date-fns-tz";

/**
 * Mapping of legacy/deprecated timezone names to their modern IANA equivalents
 */
const TIMEZONE_ALIASES: Record<string, string> = {
  // Asia
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Thimbu": "Asia/Thimphu",
  "Asia/Ujung_Pandang": "Asia/Makassar",
  "Asia/Ulan_Bator": "Asia/Ulaanbaatar",
  "Asia/Dacca": "Asia/Dhaka",
  "Asia/Ashkhabad": "Asia/Ashgabat",
  "Asia/Chungking": "Asia/Chongqing",
  "Asia/Macao": "Asia/Macau",
  "Asia/Tel_Aviv": "Asia/Jerusalem",
  "Asia/Kashgar": "Asia/Urumqi",

  // Europe
  GB: "Europe/London",
  "GB-Eire": "Europe/London",
  "Europe/Belfast": "Europe/London",
  Eire: "Europe/Dublin",
  Portugal: "Europe/Lisbon",
  Poland: "Europe/Warsaw",
  "Europe/Kiev": "Europe/Kyiv",
  "Europe/Uzhgorod": "Europe/Kyiv",
  "Europe/Zaporozhye": "Europe/Kyiv",
  "Europe/Tiraspol": "Europe/Chisinau",

  // Americas
  "US/Eastern": "America/New_York",
  "US/Central": "America/Chicago",
  "US/Mountain": "America/Denver",
  "US/Pacific": "America/Los_Angeles",
  "US/Alaska": "America/Anchorage",
  "US/Hawaii": "Pacific/Honolulu",
  "US/Arizona": "America/Phoenix",
  "Canada/Eastern": "America/Toronto",
  "Canada/Central": "America/Winnipeg",
  "Canada/Mountain": "America/Edmonton",
  "Canada/Pacific": "America/Vancouver",
  "Brazil/East": "America/Sao_Paulo",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Mendoza": "America/Argentina/Mendoza",
  "America/Santa_Isabel": "America/Tijuana",
  "America/Shiprock": "America/Denver",
  "America/Montreal": "America/Toronto",

  // Africa
  "Africa/Asmera": "Africa/Asmara",
  "Africa/Timbuktu": "Africa/Bamako",
  Egypt: "Africa/Cairo",
  Libya: "Africa/Tripoli",

  // Pacific
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
  "Pacific/Yap": "Pacific/Chuuk",
  "Pacific/Samoa": "Pacific/Pago_Pago",
  "Pacific/Johnston": "Pacific/Honolulu",

  // Atlantic
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Atlantic/Jan_Mayen": "Europe/Oslo",

  // Australia
  "Australia/ACT": "Australia/Sydney",
  "Australia/NSW": "Australia/Sydney",
  "Australia/LHI": "Australia/Lord_Howe",
  "Australia/North": "Australia/Darwin",
  "Australia/Queensland": "Australia/Brisbane",
  "Australia/South": "Australia/Adelaide",
  "Australia/Tasmania": "Australia/Hobart",
  "Australia/Victoria": "Australia/Melbourne",
  "Australia/West": "Australia/Perth",
  "Australia/Yancowinna": "Australia/Broken_Hill",

  // Antarctica
  "Antarctica/South_Pole": "Pacific/Auckland",

  // Generic/Legacy
  UTC: "Etc/UTC",
  GMT: "Etc/GMT",
  "GMT+0": "Etc/GMT",
  "GMT-0": "Etc/GMT",
  GMT0: "Etc/GMT",
  Greenwich: "Etc/GMT",
  Universal: "Etc/UTC",
  Zulu: "Etc/UTC",
  UCT: "Etc/UTC",
  "W-SU": "Europe/Moscow",
  PRC: "Asia/Shanghai",
  ROC: "Asia/Taipei",
  ROK: "Asia/Seoul",
  Japan: "Asia/Tokyo",
  Singapore: "Asia/Singapore",
  Hongkong: "Asia/Hong_Kong",
  Iceland: "Atlantic/Reykjavik",
  Jamaica: "America/Jamaica",
  Cuba: "America/Havana",
  Turkey: "Europe/Istanbul",
  Israel: "Asia/Jerusalem",
  Iran: "Asia/Tehran",
  NZ: "Pacific/Auckland",
  "NZ-CHAT": "Pacific/Chatham",
  Kwajalein: "Pacific/Kwajalein",
  Navajo: "America/Denver",
};

/**
 * List of valid IANA timezone prefixes for validation
 */
const VALID_TIMEZONE_PREFIXES = [
  "Africa/",
  "America/",
  "Antarctica/",
  "Arctic/",
  "Asia/",
  "Atlantic/",
  "Australia/",
  "Europe/",
  "Indian/",
  "Pacific/",
  "Etc/",
];

/**
 * Validates if a timezone string is a valid IANA timezone
 */
const isValidIANATimezone = (tz: string): boolean => {
  if (!tz || typeof tz !== "string") {
    return false;
  }

  // Check if it starts with a valid prefix
  const hasValidPrefix = VALID_TIMEZONE_PREFIXES.some((prefix) =>
    tz.startsWith(prefix),
  );

  if (!hasValidPrefix) {
    return false;
  }

  // Validate by attempting to use it with Intl.DateTimeFormat
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/**
 * Normalizes a timezone string to its canonical IANA form
 */
const normalizeTimezone = (tz: string): string => {
  if (!tz) return "";

  // Check if it's a known alias
  if (TIMEZONE_ALIASES[tz]) {
    return TIMEZONE_ALIASES[tz];
  }

  return tz;
};

/**
 * Gets timezone using the primary Intl API method
 */
const getTimezoneFromIntl = (): string | null => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || null;
  } catch {
    return null;
  }
};

/**
 * Gets timezone using explicit locale (fallback method)
 */
const getTimezoneWithLocale = (): string | null => {
  try {
    // Try with explicit undefined locale to get system default
    const options = Intl.DateTimeFormat(undefined, {
      timeZoneName: "long",
    }).resolvedOptions();
    return options.timeZone || null;
  } catch {
    return null;
  }
};

/**
 * Estimates timezone from UTC offset (last resort fallback)
 * Note: This is not accurate for DST-observing regions
 */
const getTimezoneFromOffset = (): string | null => {
  try {
    const offset = new Date().getTimezoneOffset();
    const hours = Math.abs(Math.floor(offset / 60));
    const sign = offset <= 0 ? "+" : "-";

    // Map common offsets to timezones (simplified)
    const offsetMap: Record<number, string> = {
      0: "Etc/UTC",
      [-60]: "Europe/London", // BST: UTC+1 (Daylight Saving)
      [-330]: "Asia/Kolkata", // IST: UTC+5:30
      [-480]: "Asia/Shanghai", // CST: UTC+8
      [-540]: "Asia/Tokyo", // JST: UTC+9
      [-600]: "Australia/Sydney", // AEST: UTC+10
      300: "America/New_York", // EST: UTC-5
      360: "America/Chicago", // CST: UTC-6
      420: "America/Denver", // MST: UTC-7
      480: "America/Los_Angeles", // PST: UTC-8
    };

    if (offsetMap[offset]) {
      return offsetMap[offset];
    }

    // Return Etc/GMT±X format as last resort
    return `Etc/GMT${sign}${hours}`;
  } catch {
    return null;
  }
};

/**
 * Detects timezone using date-fns-tz (if available)
 */
const getTimezoneFromDateFnsTz = (): string | null => {
  try {
    const now = new Date();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Validate by formatting
    format(now, "yyyy-MM-dd", { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
};

/**
 * Main timezone detection interface
 */
export interface TimezoneResult {
  timezone: string;
  isValid: boolean;
  wasNormalized: boolean;
  originalTimezone: string | null;
  detectionMethod:
    | "intl"
    | "intl-locale"
    | "date-fns-tz"
    | "offset"
    | "fallback";
  offset: number;
  offsetString: string;
}

/**
 * Enhanced timezone detection function with multiple fallbacks
 * Returns detailed information about the detected timezone
 */
export const detectTimezone = (): TimezoneResult => {
  let originalTz: string | null = null;
  let detectedTz: string | null = null;
  let detectionMethod: TimezoneResult["detectionMethod"] = "fallback";

  // Method 1: Primary Intl API
  detectedTz = getTimezoneFromIntl();
  if (detectedTz) {
    originalTz = detectedTz;
    detectionMethod = "intl";
  }

  // Method 2: Intl with explicit locale
  if (!detectedTz) {
    detectedTz = getTimezoneWithLocale();
    if (detectedTz) {
      originalTz = detectedTz;
      detectionMethod = "intl-locale";
    }
  }

  // Method 3: date-fns-tz validation
  if (!detectedTz) {
    detectedTz = getTimezoneFromDateFnsTz();
    if (detectedTz) {
      originalTz = detectedTz;
      detectionMethod = "date-fns-tz";
    }
  }

  // Method 4: Offset-based estimation
  if (!detectedTz) {
    detectedTz = getTimezoneFromOffset();
    if (detectedTz) {
      originalTz = detectedTz;
      detectionMethod = "offset";
    }
  }

  // Normalize the timezone
  const normalizedTz = normalizeTimezone(detectedTz || "");
  const wasNormalized = normalizedTz !== detectedTz && detectedTz !== null;

  // Validate the final timezone
  const finalTz = normalizedTz || "Etc/UTC";
  const isValid = isValidIANATimezone(finalTz);

  // Calculate offset
  const offset = new Date().getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offset / 60));
  const offsetMinutes = Math.abs(offset % 60);
  const offsetSign = offset <= 0 ? "+" : "-";
  const offsetString = `UTC${offsetSign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutes).padStart(2, "0")}`;

  return {
    timezone: isValid ? finalTz : "Etc/UTC",
    isValid,
    wasNormalized,
    originalTimezone: originalTz,
    detectionMethod,
    offset,
    offsetString,
  };
};

/**
 * Simple timezone getter - drop-in replacement for your original function
 * Returns just the timezone string
 */
export const getCurrentSystemTimezone = (): string => {
  try {
    const result = detectTimezone();
    return result.timezone;
  } catch (error) {
    console.error("Error detecting timezone:", error);
    return "Etc/UTC";
  }
};

/**
 * Gets timezone with validation - throws if invalid
 */
export const getValidatedTimezone = (): string => {
  const result = detectTimezone();

  if (!result.isValid) {
    throw new Error(`Invalid timezone detected: ${result.originalTimezone}`);
  }

  return result.timezone;
};

/**
 * Checks if a given timezone string is valid
 */
export const isValidTimezone = (tz: string): boolean => {
  return isValidIANATimezone(normalizeTimezone(tz));
};

/**
 * Gets the current UTC offset in minutes
 */
export const getCurrentUTCOffset = (): number => {
  return new Date().getTimezoneOffset();
};

/**
 * Gets a formatted offset string (e.g., "UTC+05:30")
 */
export const getFormattedOffset = (): string => {
  const result = detectTimezone();
  return result.offsetString;
};
